/**
 * «Изменить с ИИ» в окне «Просмотр макета» — API-клиент.
 * Контракт: docs/SPEC_2026-10-21_DESIGN_AI_EDIT.md (гейтвей /design-ai-edits).
 */

import { api } from '../../shared/api/client'
import type { ReplaceDesignSourceMode, ReplaceDesignSourceStart } from './api'

/** Гейтвей отдаёт { success, data } — разворачиваем до data. */
const unwrap = (r: any) => r.data?.data ?? r.data

export type DesignAiEditStatus = 'queued' | 'running' | 'succeeded' | 'failed' | 'applied'
export type DesignAiEditMergeMode = 'changes' | 'full'
export type DesignAiEditVerdict = 'ok' | 'warn' | 'fail'

export interface DesignAiEditCheckItem {
  key: string
  label: string
  status: DesignAiEditVerdict
  detail: string
}

export interface DesignAiEdit {
  id: string
  designCode: string
  prompt: string
  mergeMode: DesignAiEditMergeMode
  baseEditId: string | null
  status: DesignAiEditStatus
  model: string | null
  reservedUsd: number
  costUsd: number | null
  error: { code: string; message: string } | null
  check: { verdict: DesignAiEditVerdict; changedPercent: number; items: DesignAiEditCheckItem[] } | null
  /** Относительные /api/v1/... URL; грузить через авторизованный транспорт изображений. */
  files: { source: string | null; result: string | null; overlay: string | null }
  createdAt: string
  createdBy: number
  appliedAt: string | null
  applyJobId: string | null
  applyMode: string | null
}

export interface DesignAiEditModel {
  id: string
  name: string
  provider: string
  model: string
  isDefault: boolean
  reserveUsd: number
}

export interface DesignAiEditStartBody {
  designCode: number | string
  prompt: string
  bindingId?: string
  mergeMode: DesignAiEditMergeMode
  baseEditId?: string
}

export interface DesignAiEditApplyBody {
  mode: ReplaceDesignSourceMode
  resend_photos: boolean
  accept_warnings: boolean
}

export const designAiEditAPI = {
  listModels: (): Promise<DesignAiEditModel[]> =>
    api.get('/design-ai-edits/models').then(unwrap),
  listForDesign: (designCode: number | string): Promise<DesignAiEdit[]> =>
    api.get('/design-ai-edits', { params: { designCode } }).then(unwrap),
  start: (body: DesignAiEditStartBody, idempotencyKey: string): Promise<DesignAiEdit> =>
    api.post('/design-ai-edits', body, { headers: { 'Idempotency-Key': idempotencyKey } }).then(unwrap),
  get: (id: string): Promise<DesignAiEdit> =>
    api.get(`/design-ai-edits/${id}`).then(unwrap),
  apply: (
    id: string,
    body: DesignAiEditApplyBody,
    idempotencyKey: string,
  ): Promise<ReplaceDesignSourceStart> =>
    api.post(`/design-ai-edits/${id}/apply`, body, { headers: { 'Idempotency-Key': idempotencyKey } }).then(unwrap),
}
